import { Component, OnInit, OnDestroy, signal, WritableSignal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertService, AlertMessage } from '../../services/alert.service';
import { Subscription } from 'rxjs';

interface DisplayAlert extends AlertMessage {
  id: number;
  fadeOut: boolean;
  remainingTime: number;
}

@Component({
  selector: 'app-alert',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss']
})
export class AlertComponent implements OnInit, OnDestroy {
  alerts: WritableSignal<DisplayAlert[]> = signal([]);
  private subscription: Subscription = new Subscription();
  private alertCounter = 0;
  private intervals: Map<number, any> = new Map();

  constructor(
    private alertService: AlertService,
    private cdr: ChangeDetectorRef
  ) {}

  trackByFn(index: number, item: DisplayAlert): number {
    return item.id;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.alertService.alerts$.subscribe(alert => {
        this.addAlert(alert);
      })
    );
  }

  addAlert(alert: AlertMessage): void {
    const id = ++this.alertCounter;
    const newAlert: DisplayAlert = {
      ...alert,
      id,
      fadeOut: false,
      remainingTime: 3
    };

    // Mesajı dərhal siyahıya əlavə edirik (unshift ilə ən üstə)
    this.alerts.update(current => [newAlert, ...current]);

    // Geri sayım taymeri
    const interval = setInterval(() => {
      let shouldRemove = false;

      this.alerts.update(current => {
        const alertToUpdate = current.find(a => a.id === id);
        if (!alertToUpdate) return current;

        if (alertToUpdate.remainingTime > 1) {
          return current.map(a =>
            a.id === id ? { ...a, remainingTime: a.remainingTime - 1 } : a
          );
        } else {
          // 1 olan zaman 0-a keçirik və dərhal silinməni işə salırıq
          shouldRemove = true;
          return current.map(a =>
            a.id === id ? { ...a, remainingTime: 0 } : a
          );
        }
      });

      if (shouldRemove) {
        this.removeAlert(id);
      }
    }, 1000);

    this.intervals.set(id, interval);
  }

  removeAlert(id: number): void {
    this.clearLocalInterval(id);

    const currentAlerts = this.alerts();
    const alertIndex = currentAlerts.findIndex(a => a.id === id);

    if (alertIndex !== -1 && !currentAlerts[alertIndex].fadeOut) {
      // Fade out effekti üçün markalayırıq
      this.alerts.update(current =>
        current.map(a => a.id === id ? { ...a, fadeOut: true } : a)
      );

      // Animasiya bitdikdən sonra silirik
      setTimeout(() => {
        this.alerts.update(current => current.filter(a => a.id !== id));
      }, 300);
    }
  }

  private clearLocalInterval(id: number): void {
    const interval = this.intervals.get(id);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(id);
    }
  }

  getIcon(type: string): string {
    switch (type) {
      case 'success': return 'fa-solid fa-circle-check';
      case 'error': return 'fa-solid fa-circle-exclamation';
      case 'warning': return 'fa-solid fa-triangle-exclamation';
      case 'info': return 'fa-solid fa-circle-info';
      default: return 'fa-solid fa-bell';
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals.clear();
  }
}
